import { Fragment, useEffect, useMemo, useState } from 'react'
import { api, type Finding, type PendingRule, type SourceDocument } from '../api'
import { FilterHeader, SortHeader, sortRows, useSort } from '../components/Grid'
import { RowMenu } from '../components/RowMenu'
import { layerLabel, says, valueText } from '../limits'
import { DeskCell, DeskHeader, useDesks } from '../desks'

/**
 * The clause review — TECHNICAL_DESIGN.md §7.1.
 *
 * The stage between staging a workbook and signing it off:
 *
 * > **For a customer migrating an existing workbook, the mandate document is the
 * > check, not a second input.** The workbook supplies the value; the IMA
 * > confirms it and supplies the citation. The valuable output of that pass is
 * > therefore the **disagreements**.
 *
 * So the screen is built around making disagreeing as easy as confirming. A
 * review UI where *confirm* is one click and *this is wrong* is a form nobody
 * fills in produces a migration that agrees with itself — which is precisely the
 * outcome §7.1 warns about, and the one that generates no complaint from anyone.
 *
 * Both actions ask for the clause, because both are statements about a document.
 * A disagreement still cites: the document is still where the rule came from,
 * and leaving the candidate unsourced would trade one problem for another.
 *
 * **Many can be confirmed at once.** One schedule usually settles a dozen limits,
 * and the reading is what takes the time; asking for the clause twelve times does
 * not make anybody read it twelve times. Disagreeing stays one at a time, because
 * a finding carries what the document says instead and what the reviewer found,
 * and those differ for every limit by definition.
 *
 * The document is **chosen from the registry**, not typed. It was once asked for
 * as an id in a browser prompt, which no screen could tell anybody — so the
 * queue could not be worked at all. Documents are added on *Mandate documents*.
 *
 * **Each table filters from its own Portfolio header.** The control belongs on
 * the column it acts on — that is where the reader asks what the column is
 * showing. The findings heading keeps counting *all* of them while filtered,
 * because a blocker hidden by a control the reader set is still a blocker.
 */

function appliesTo(rule: PendingRule): string {
  return rule.portfolio_code ?? rule.scope_code ?? 'Unattached'
}

export function ClauseReview() {
  const [queue, setQueue] = useState<PendingRule[]>([])
  const [findings, setFindings] = useState<Finding[]>([])
  const [docs, setDocs] = useState<SourceDocument[]>([])
  /** The candidate being cited, and whether the document agrees with it. */
  const [citing, setCiting] = useState<{ rule: PendingRule; agrees: boolean } | null>(null)
  const [form, setForm] = useState({ documentId: '', clause: '', says: '', note: '' })
  const [picked, setPicked] = useState<Set<string>>(new Set())
  const [bulk, setBulk] = useState({ documentId: '', clause: '' })
  const [outcome, setOutcome] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  /** How far through a bulk citation we are. One request per rule, and a
   *  schedule that settles twenty is twenty round trips. */
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null)
  /* One per table. They are two lists at two stages — what is blocked, and what
     is still to read — and a reader narrowing the findings to one fund is not
     necessarily narrowing what they intend to work through next. */
  const [foundFund, setFoundFund] = useState('')
  const [fund, setFund] = useState('')
  const [layer, setLayer] = useState('')
  const found = useSort()
  const candidates = useSort()
  /** Each table's Desk column, for somebody across the whole firm (decision 91). */
  const [foundDesk, setFoundDesk] = useState('')
  const [desk, setDesk] = useState('')
  const desks = useDesks()
  const deskOfRule = (r: PendingRule) =>
    (r.portfolio_code ? desks.fund(r.portfolio_code) : desks.group(r.scope_code)) ?? ''

  async function load() {
    setError(null)
    try {
      const [pending, open, registry] = await Promise.all([
        api.pendingRules(),
        api.clauseFindings(),
        api.documents(),
      ])
      setDocs(registry.filter((d) => d.status !== 'superseded'))
      // Only candidates that still need the pass. One already cited to a
      // mandate document and undisputed is an approver's problem, not a
      // reviewer's.
      //
      // A rule citing its guideline *pack* is not that. The pack is the
      // client's own spreadsheet — it says where the limit came from, which is
      // all §5.5 asks — and §7.1 is explicit that the mandate document is the
      // check rather than a second input. So a pack-cited rule is exactly what
      // this queue is for, and dropping it because `source_document_id` is
      // non-null would have emptied the screen the moment packs shipped.
      const blocked = new Set(open.map((f) => f.rule_id))
      setQueue(
        pending.filter(
          (p) =>
            !p.source_document_id ||
            p.source_document_kind === 'pack' ||
            blocked.has(p.rule_id),
        ),
      )
      setFindings(open)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }

  useEffect(() => {
    void load()
  }, [])

  function begin(rule: PendingRule, agrees: boolean) {
    setError(null)
    setForm({
      // One document in the registry is the common case early on, and making
      // somebody choose from a list of one teaches nothing.
      documentId: docs.length === 1 ? (docs[0]?.id ?? '') : '',
      clause: '',
      says: '',
      note: '',
    })
    setCiting({ rule, agrees })
  }

  function toggle(ruleId: string) {
    setPicked((was) => {
      const next = new Set(was)
      if (!next.delete(ruleId)) next.add(ruleId)
      return next
    })
  }

  /** Each column offers the values its own table actually holds, so an empty
   *  result is never something the control itself produced. */
  const foundFunds = useMemo(
    () =>
      [...new Set(findings.map((f) => f.portfolio_code ?? 'Unattached'))].sort((a, b) =>
        a.localeCompare(b),
      ),
    [findings],
  )
  const funds = useMemo(
    () => [...new Set(queue.map(appliesTo))].sort((a, b) => a.localeCompare(b)),
    [queue],
  )
  const layers = useMemo(
    () => [...new Set(queue.map((r) => r.layer))].sort((a, b) => a.localeCompare(b)),
    [queue],
  )

  const shownFindings = useMemo(() => {
    const kept = findings.filter(
      (f) =>
        (!foundFund || (f.portfolio_code ?? 'Unattached') === foundFund) &&
        (!foundDesk || desks.fund(f.portfolio_code) === foundDesk),
    )
    return sortRows(kept, found.sort, {
      desk: (f) => desks.fund(f.portfolio_code) ?? '',
      fund: (f) => f.portfolio_code ?? 'Unattached',
      limit: (f) => f.rule_key,
    })
  }, [findings, foundFund, found.sort, foundDesk, desks])

  const rows = useMemo(() => {
    const kept = queue.filter(
      (r) => (!fund || appliesTo(r) === fund) && (!layer || r.layer === layer) && (!desk || deskOfRule(r) === desk),
    )
    return sortRows(kept, candidates.sort, {
      desk: deskOfRule,
      fund: appliesTo,
      limit: (r) => r.rule_key,
      layer: (r) => layerLabel(r.layer),
    })
  }, [queue, fund, layer, candidates.sort, desk, desks])

  /* Bulk cites what is on screen. A row a filter has hidden is a row nobody
     read the clause against. */
  const chosen = rows.filter((r) => picked.has(r.rule_id))

  /**
   * Cite one clause against everything ticked.
   *
   * The reading is what takes the time, and one schedule usually settles a dozen
   * limits at once. Confirming them one at a time does not make somebody read
   * twelve times — so the clause is asked for once and recorded against each,
   * which is exactly what the person did. What is *not* offered in bulk is
   * disagreeing: a finding carries what the document says instead and what the
   * reviewer found, and those are different for every limit by definition.
   */
  async function citeMany() {
    if (chosen.length === 0 || !bulk.documentId || !bulk.clause.trim()) return

    setBusy(true)
    setError(null)
    setOutcome(null)
    setProgress({ done: 0, total: chosen.length })
    let done = 0
    const refused: string[] = []

    // One request each, and a dozen of those is long enough that a screen
    // showing nothing looks broken — the first version greyed the button, sat
    // there, and then emptied the whole queue at once, which reads as a page
    // that reloaded on its own rather than as work somebody asked for.
    for (const rule of chosen) {
      try {
        await api.cite(rule.rule_id, bulk.documentId, bulk.clause.trim())
        done += 1
      } catch (e) {
        refused.push(`${rule.rule_key}: ${e instanceof Error ? e.message : String(e)}`)
      }
      setProgress({ done: done + refused.length, total: chosen.length })
    }

    setPicked(new Set())
    setBulk({ documentId: bulk.documentId, clause: '' })
    setOutcome(
      refused.length === 0
        ? `${done} cited to that clause. They have left this queue and are now ` +
          'waiting for an approver.'
        : `${done} cited, ${refused.length} refused — ${refused.join('; ')}`,
    )
    setBusy(false)
    setProgress(null)
    await load()
  }

  async function submit() {
    if (!citing) return
    const { rule, agrees } = citing
    setBusy(true)
    setError(null)
    try {
      if (agrees) {
        await api.cite(rule.rule_id, form.documentId, form.clause.trim())
      } else {
        await api.disagree(rule.rule_id, {
          // Silence is a different finding from a different number, and the two
          // are answered differently: one is a gap, the other a contradiction.
          kind: form.says.trim() ? 'threshold_differs' : 'document_silent',
          source_document_id: form.documentId,
          source_clause: form.clause.trim(),
          document_value: form.says.trim() ? Number(form.says) : null,
          note: form.note.trim(),
        })
      }
      setCiting(null)
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  async function answer(finding: Finding, resolution: string) {
    const note = window.prompt(
      resolution === 'workbook_wins'
        ? 'This overrides the document the firm is bound by. Name what supports it — ' +
          'typically a later side letter the schedule predates.'
        : 'What was decided?',
    )
    if (!note?.trim()) return
    setError(null)
    try {
      await api.resolveFinding(finding.id, resolution, note.trim())
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }

  return (
    <div className="clause-review">
      <header className="page-head">
        <h2>Clause review</h2>
        <p className="note">
          The mandate document itself, clause by clause: which became rules, which
          were marked as needing none, and which are still to decide. A clause with
          no rule is the gap an auditor asks about.
        </p>
        <p className="note">
          The workbook supplied the value. The mandate document confirms it and
          supplies the citation — and where they differ, that is a finding in a
          document the firm is contractually bound by.
        </p>
      </header>

      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}

      {findings.length > 0 && (
        <section className="warn" data-testid="findings">
          {/* The total, always. A blocker the reader filtered out of sight is
              still a blocker, and a heading that counted only the visible ones
              would say "0 unanswered" over a screen that cannot be signed off. */}
          <h3>{findings.length} unanswered finding(s)</h3>
          <p>These block sign-off until somebody decides which is right.</p>
          {shownFindings.length === 0 ? (
            <p className="empty">None on {foundFund}.</p>
          ) : (
            <table className="grid findings has-menus">
              <colgroup>
                {desks.firmWide && <col className="col-desk" />}
                <col className="col-portfolio" />
                <col className="col-limit" />
                <col className="col-values" />
                <col className="col-wide" />
                <col className="col-menu" />
              </colgroup>
              <thead>
                <tr>
                  <DeskHeader sort={found.sort} onSort={found.onSort} value={foundDesk} onChange={setFoundDesk} />
                  <FilterHeader
                    label="Portfolio"
                    sortKey="fund"
                    sort={found.sort}
                    onSort={found.onSort}
                    value={foundFund}
                    onChange={setFoundFund}
                    all="Every portfolio"
                    testId="filter-finding-portfolio"
                    options={foundFunds.map((f) => ({ value: f, label: f }))}
                  />
                  <SortHeader
                    label="Limit"
                    sortKey="limit"
                    sort={found.sort}
                    onSort={found.onSort}
                  />
                  <th>Workbook &middot; document</th>
                  <th>What was found</th>
                  <th>Decision</th>
                </tr>
              </thead>
              <tbody>
                {shownFindings.map((f) => (
                  <tr key={f.id} data-testid="finding">
                    <DeskCell fund={f.portfolio_code} />
                    <td>
                      <span className="portfolio">{f.portfolio_code}</span>
                    </td>
                    <td>
                      <code>{f.rule_key}</code>
                    </td>
                    {/* Both values through the same formatter as the queue: a
                        range threshold here printed `[object Object]` in the one
                        place a reviewer is comparing two numbers. */}
                    <td className="limit" data-testid="finding-values">
                      workbook {valueText(f.workbook_value)} &middot; document{' '}
                      {f.document_value === null ? 'silent' : valueText(f.document_value)}
                    </td>
                    <td>
                      <span className="note">{f.note}</span>
                      {f.source_clause && <span className="clause">{f.source_clause}</span>}
                    </td>
                    <td className="actions">
                      <RowMenu
                        label={`Answer the finding on ${f.rule_key}`}
                        testId="finding-menu"
                        items={[
                          {
                            label: 'Document wins',
                            onSelect: () => void answer(f, 'document_wins'),
                          },
                          {
                            label: 'Workbook wins',
                            onSelect: () => void answer(f, 'workbook_wins'),
                          },
                          {
                            label: 'Withdraw',
                            danger: true,
                            onSelect: () => void answer(f, 'withdrawn'),
                          },
                        ]}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      )}

      {docs.length === 0 && (
        <p className="warn" data-testid="no-documents">
          No documents are registered yet, so there is nothing to check these
          against. Add the agreement or letter on <strong>Mandate documents</strong>{' '}
          first.
        </p>
      )}

      <h3>Candidates awaiting the document</h3>

      {rows.length > 0 && docs.length > 0 && (
        <form
          className="bulk-bar"
          data-testid="bulk-cite"
          onSubmit={(e) => {
            e.preventDefault()
            void citeMany()
          }}
        >
          <span>
            {progress
              ? `Citing — ${progress.done} of ${progress.total} sent…`
              : chosen.length === 0
                ? 'Read the document, tick everything it settles, cite it once.'
                : `${chosen.length} selected`}
          </span>
          {progress && (
            <progress
              data-testid="bulk-progress"
              max={progress.total}
              value={progress.done}
              aria-label={`${progress.done} of ${progress.total} sent`}
            />
          )}
          <label>
            Document
            <select
              value={bulk.documentId}
              onChange={(e) => setBulk({ ...bulk, documentId: e.target.value })}
            >
              <option value="">Which document?</option>
              {docs.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.title}
                  {d.version_label ? ` · ${d.version_label}` : ''}
                </option>
              ))}
            </select>
          </label>
          <label>
            Clause
            <input
              value={bulk.clause}
              placeholder="Sch. 2 para 4(b)"
              title="Recorded against every limit you have ticked. Where they come from different clauses, do them in groups."
              onChange={(e) => setBulk({ ...bulk, clause: e.target.value })}
            />
          </label>
          <button
            type="submit"
            disabled={busy || chosen.length === 0 || !bulk.documentId || !bulk.clause.trim()}
          >
            Confirm selected
          </button>
          {chosen.length > 0 && (
            <button type="button" className="link" onClick={() => setPicked(new Set())}>
              Clear
            </button>
          )}
        </form>
      )}

      {/* Directly under the control that was just used. It used to sit above the
          heading, several sections up: after a bulk citation the form vanished —
          every row it acted on had left the queue — and the only thing left
          where the reader was looking was an empty table, which reads as the
          page having reloaded by itself rather than as the work having been
          done. */}
      {outcome && (
        <p className="ok" data-testid="bulk-outcome">
          {outcome}
        </p>
      )}

      {queue.length === 0 ? (
        <p className="empty">
          {outcome
            ? 'Nothing left awaiting a document — everything here has been cited.'
            : 'Nothing awaiting review.'}
        </p>
      ) : rows.length === 0 ? (
        <p className="empty">No candidate matches those filters.</p>
      ) : (
        <table className="grid candidates has-menus">
          <colgroup>
            <col className="col-tick" />
            {desks.firmWide && <col className="col-desk" />}
            <col className="col-portfolio" />
            <col className="col-limit" />
            <col className="col-layer" />
            <col className="col-says" />
            <col className="col-source" />
            <col className="col-menu" />
          </colgroup>
          <thead>
            <tr>
              <th className="tick">
                <input
                  type="checkbox"
                  aria-label="Select every candidate"
                  checked={rows.length > 0 && chosen.length === rows.length}
                  onChange={(e) =>
                    setPicked(e.target.checked ? new Set(rows.map((r) => r.rule_id)) : new Set())
                  }
                />
              </th>
              <DeskHeader sort={candidates.sort} onSort={candidates.onSort} value={desk} onChange={setDesk} />
              <FilterHeader
                label="Portfolio"
                sortKey="fund"
                sort={candidates.sort}
                onSort={candidates.onSort}
                value={fund}
                onChange={setFund}
                all="Every portfolio"
                testId="filter-portfolio"
                options={funds.map((f) => ({ value: f, label: f }))}
              />
              <SortHeader
                label="Limit"
                sortKey="limit"
                sort={candidates.sort}
                onSort={candidates.onSort}
              />
              <FilterHeader
                label="Set by"
                sortKey="layer"
                sort={candidates.sort}
                onSort={candidates.onSort}
                value={layer}
                onChange={setLayer}
                all="Every layer"
                testId="filter-layer"
                options={layers.map((l) => ({ value: l, label: layerLabel(l) }))}
              />
              <th>Says</th>
              <th>Document</th>
              <th>Decision</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((rule) => {
              const said = says(rule)

              return (
                <Fragment key={rule.rule_id}>
                  <tr data-testid="candidate">
                    <td className="tick">
                      <input
                        type="checkbox"
                        aria-label={`Select ${rule.rule_key}`}
                        checked={picked.has(rule.rule_id)}
                        onChange={() => toggle(rule.rule_id)}
                      />
                    </td>
                    <DeskCell fund={rule.portfolio_code} group={rule.scope_code} />
                    <td>
                      <span className="portfolio">{appliesTo(rule)}</span>
                    </td>
                    <td>
                      <code>{rule.rule_key}</code>
                      {rule.measure && <span className="measure">{rule.measure}</span>}
                    </td>
                    <td>
                      <span className="pill pill-layer">{layerLabel(rule.layer)}</span>
                    </td>
                    <td>
                      <span className={said.numeric ? 'limit' : 'no-number'}>{said.text}</span>
                    </td>
                    <td>
                      {rule.source_document_kind === 'pack' ? (
                        /* Cited, and to the wrong sort of thing. It can be
                           approved — the provenance is honest as far as it goes
                           — and this queue exists to take it the rest of the
                           way, so the row says which half is done. */
                        <a
                          className="flag"
                          data-testid="pack-only"
                          href={`/api/documents/${rule.source_document_id}/content`}
                          title="Opens the workbook this was read out of"
                        >
                          ⚑ {rule.source_clause ? `cell ${rule.source_clause} of ` : ''}
                          {rule.source_document_title ?? 'the client’s spreadsheet'} — not
                          the mandate
                        </a>
                      ) : rule.source_document_id ? (
                        <span className="cited">
                          {rule.source_clause ? `${rule.source_clause} of ` : 'cited to '}
                          {rule.source_document_title ?? 'a document'}
                        </span>
                      ) : (
                        <span className="warn" data-testid="uncited">
                          cites nothing &mdash; cannot be approved
                        </span>
                      )}
                      {/* Only once the citation has moved on from the pack.
                          While the two are the same document the line above is
                          already a link to it, and the cell named one file
                          twice. */}
                      {rule.origin_document_id &&
                        rule.origin_document_id !== rule.source_document_id && (
                          <a
                            className="pack-link"
                            href={`/api/documents/${rule.origin_document_id}/content`}
                            title="Opens the workbook this was read out of"
                          >
                            read out of{' '}
                            {rule.source_anchor ? `cell ${rule.source_anchor} of ` : ''}
                            {rule.origin_document_title ?? 'an imported workbook'}
                          </a>
                        )}
                    </td>
                    {/* Equal weight, deliberately. A review where "confirm" is one
                        click and "this is wrong" is a form nobody fills in produces
                        a migration that agrees with itself — so the two sit side by
                        side in the menu, in that order, neither promoted. */}
                    <td className="actions">
                      <RowMenu
                        label={`Record what the document says about ${rule.rule_key}`}
                        testId="candidate-menu"
                        items={[
                          {
                            label: 'Document confirms it',
                            disabled: busy || docs.length === 0,
                            why:
                              docs.length === 0
                                ? 'No document is registered to check it against. Add one on Mandate documents.'
                                : undefined,
                            onSelect: () => begin(rule, true),
                          },
                          {
                            label: 'Document says otherwise',
                            disabled: busy || docs.length === 0,
                            why:
                              docs.length === 0
                                ? 'No document is registered to check it against. Add one on Mandate documents.'
                                : undefined,
                            onSelect: () => begin(rule, false),
                          },
                        ]}
                      />
                    </td>
                  </tr>

                  {citing?.rule.rule_id === rule.rule_id && (
                    <tr className="cite-row">
                      <td colSpan={7 + (desks.firmWide ? 1 : 0)}>
                        <form
                          className="cite-form"
                          data-testid="cite-form"
                          onSubmit={(e) => {
                            e.preventDefault()
                            void submit()
                          }}
                        >
                          <label>
                            Document
                            <select
                              value={form.documentId}
                              required
                              onChange={(e) => setForm({ ...form, documentId: e.target.value })}
                            >
                              <option value="">Which document did you read?</option>
                              {docs.map((d) => (
                                <option key={d.id} value={d.id}>
                                  {d.title}
                                  {d.version_label ? ` · ${d.version_label}` : ''}
                                </option>
                              ))}
                            </select>
                          </label>
                          <label>
                            Clause
                            <input
                              value={form.clause}
                              required
                              placeholder="Sch. 2 para 4(b)"
                              title="Where in the document this is written. An auditor should be able to turn to it."
                              onChange={(e) => setForm({ ...form, clause: e.target.value })}
                            />
                          </label>
                          {!citing.agrees && (
                            <>
                              <label>
                                What it says
                                <input
                                  value={form.says}
                                  placeholder="Leave blank if silent"
                                  title="The number in the document, if it gives one. Blank means the document does not cover this."
                                  onChange={(e) => setForm({ ...form, says: e.target.value })}
                                />
                              </label>
                              <label>
                                What you found
                                <input
                                  value={form.note}
                                  required
                                  title="This is what the next person reads before deciding which is right."
                                  onChange={(e) => setForm({ ...form, note: e.target.value })}
                                />
                              </label>
                            </>
                          )}
                          <button type="submit" disabled={busy}>
                            {citing.agrees ? 'Cite it' : 'Raise it'}
                          </button>
                          <button type="button" onClick={() => setCiting(null)}>
                            Cancel
                          </button>
                        </form>
                      </td>
                    </tr>
                  )}
                </Fragment>
              )
            })}
          </tbody>
        </table>
      )}
    </div>
  )
}
